/**
 * CHM005 工地量房 PWA — 主入口。
 * 三個 scene：HOME / EDIT / RESULT
 */

import { FreePolygon, estimateFlooring, FlooringSpec } from './lib/polygon';
import { PolygonEditor, approximateCircle } from './scenes/PolygonEditor';
import { WebXRScanner } from './scenes/WebXRScanner';

const app = document.getElementById('app')!;

// mock SKU data — Phase 2 改接 CHM004 API
const MOCK_SKUS = [
  { id: 'SPC-1219-184-50', name: 'SPC 標準款 1219×184×5.0mm', priceTwdPerPiece: 89, lossRatePct: 11 },
  { id: 'SPC-1524-229-60', name: 'SPC 寬長板 1524×229×6.0mm', priceTwdPerPiece: 145, lossRatePct: 11 },
  { id: 'LAM-1220-196-12', name: '超耐磨 1220×196×12mm', priceTwdPerPiece: 180, lossRatePct: 13 },
];

interface State {
  scene: 'home' | 'edit' | 'result';
  polygon: FreePolygon | null;
  selectedSkuId: string;
}

const state: State = {
  scene: 'home',
  polygon: null,
  selectedSkuId: MOCK_SKUS[0].id,
};

function render() {
  app.innerHTML = '';
  if (state.scene === 'home') { void renderHome(); }
  else if (state.scene === 'edit') renderEdit();
  else renderResult();
}

async function renderHome() {
  const xrOK = await WebXRScanner.isSupported();
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  const platHint = isIOS
    ? '📱 你在 iPhone — Apple 鎖死 WebXR、請用矩形 / 圓形手繪。<br/>iPhone Pro 有 LiDAR、之後做 native app 銜接（CHM003）。'
    : xrOK
      ? '✅ 偵測到 Android Chrome + WebXR 支援、可走 ARCore 量房'
      : '⚠️ 此瀏覽器不支援 WebXR、請用 Android Chrome 88+';

  app.innerHTML = `
    <div class="container">
      <header>
        <h1>冠軍欣業 · 工地量房</h1>
        <p class="muted">CHM005 · 同事內部工具</p>
      </header>
      <div class="card">
        <h2>選擇起始方式</h2>
        <button id="btn-scan" class="primary" ${xrOK ? '' : 'disabled'}>
          📱 ARCore 量房 ${xrOK ? '' : '（僅 Android 公務機）'}
        </button>
        <button id="btn-rect" class="secondary">▭ 從 4×3m 矩形開始</button>
        <button id="btn-circle" class="secondary">○ 從圓形開始</button>
      </div>
      <div class="card" style="background:${isIOS ? '#fef3c7' : '#e0f2fe'};">
        <p style="margin:0;font-size:13px;line-height:1.6;">${platHint}</p>
      </div>
      <footer class="muted">
        ±5cm 精度、報價估料用、訂製品仍須捲尺複丈
      </footer>
    </div>
  `;
  const scanBtn = document.getElementById('btn-scan') as HTMLButtonElement;
  if (xrOK) scanBtn.addEventListener('click', startScan);
  document.getElementById('btn-rect')!.addEventListener('click', () => {
    state.polygon = FreePolygon.rectangle(4, 3);
    state.scene = 'edit';
    render();
  });
  document.getElementById('btn-circle')!.addEventListener('click', () => {
    state.polygon = approximateCircle(4, 16);
    state.scene = 'edit';
    render();
  });
}

async function startScan() {
  const supported = await WebXRScanner.isSupported();
  if (!supported) {
    alert('此裝置不支援 WebXR\n請用 Android Chrome 88+\n或選矩形 / 圓形手繪');
    return;
  }
  const scanner = new WebXRScanner({
    onFinish: (r) => {
      state.polygon = r.polygon;
      state.scene = 'edit';
      render();
    },
    onCancel: () => { /* 留在 home */ },
  });
  scanner.start();
}

function renderEdit() {
  app.innerHTML = `
    <div class="container">
      <header class="row">
        <button id="back">← 重來</button>
        <h2>編輯房型</h2>
        <button id="next" class="primary">算料 →</button>
      </header>
      <div id="editor" class="editor-wrap"></div>
      <div class="row" style="gap:8px;margin-top:8px;flex-wrap:wrap;">
        <label class="chip">
          <input type="checkbox" id="ortho" checked />
          <span>📐 直角模式</span>
        </label>
        <label class="chip">
          <input type="checkbox" id="snap" checked />
          <span>📍 5cm 鎖點</span>
        </label>
        <div id="stats" class="muted small" style="margin-left:auto;"></div>
      </div>
      <div class="row" style="gap:8px;margin-top:8px;">
        <button id="rst-rect" class="small">重設矩形</button>
        <button id="rst-circle" class="small">重設圓形</button>
      </div>
      <p class="muted small">拖角點移位 · 點邊插入新角點 · 雙擊角點刪除</p>
    </div>
  `;
  const editorEl = document.getElementById('editor')!;
  const stats = document.getElementById('stats')!;
  const editor = new PolygonEditor({
    container: editorEl,
    initial: state.polygon!,
    onChange: (p) => updateStats(p, stats),
  });
  updateStats(state.polygon!, stats);
  document.getElementById('back')!.addEventListener('click', () => { state.scene = 'home'; render(); });
  document.getElementById('next')!.addEventListener('click', () => {
    state.polygon = editor.getPolygon();
    state.scene = 'result';
    render();
  });
  document.getElementById('rst-rect')!.addEventListener('click', () => {
    editor.reset(FreePolygon.rectangle(4, 3));
    updateStats(editor.getPolygon(), stats);
  });
  document.getElementById('rst-circle')!.addEventListener('click', () => {
    editor.reset(approximateCircle(4, 16));
    updateStats(editor.getPolygon(), stats);
  });
  document.getElementById('ortho')!.addEventListener('change', (e) => {
    editor.orthoMode = (e.target as HTMLInputElement).checked;
  });
  document.getElementById('snap')!.addEventListener('change', (e) => {
    editor.gridSnap = (e.target as HTMLInputElement).checked;
  });
}

function updateStats(p: FreePolygon, el: HTMLElement) {
  el.textContent = `${p.areaM2.toFixed(2)} m² · 周長 ${p.perimeterM.toFixed(2)} m · ${p.vertices.length} 角`;
}

function renderResult() {
  const poly = state.polygon!;
  const sku = MOCK_SKUS.find(s => s.id === state.selectedSkuId)!;
  const [lenStr, widStr] = sku.id.split('-').slice(1);
  const lenMm = Number(lenStr), widMm = Number(widStr);
  const spec: FlooringSpec = {
    plankLengthMm: lenMm, plankWidthMm: widMm, lossRatePct: sku.lossRatePct,
  };
  const e = estimateFlooring(poly, spec);
  const totalTwd = e.plankCount * sku.priceTwdPerPiece;

  app.innerHTML = `
    <div class="container">
      <header class="row">
        <button id="back">← 改房型</button>
        <h2>料量 / 報價</h2>
        <button id="export" class="primary">📄 出 PDF</button>
      </header>
      <div class="card">
        <h3>規格</h3>
        <select id="sku">
          ${MOCK_SKUS.map(s => `<option value="${s.id}" ${s.id === sku.id ? 'selected' : ''}>${s.name}</option>`).join('')}
        </select>
      </div>
      <div class="card">
        <h3>料量</h3>
        <table>
          <tr><td>地面</td><td>${e.areaM2.toFixed(2)} m²</td></tr>
          <tr><td>板數（含 ${e.lossRatePct}% 損料）</td><td><b>${e.plankCount}</b> 片</td></tr>
          <tr><td>PE 靜音底料</td><td>${e.peUnderlaymentM2.toFixed(2)} m²</td></tr>
          <tr><td>矽利康</td><td>${e.siliconeTubes} 條</td></tr>
          <tr><td>踢腳板</td><td>${e.skirtingM.toFixed(2)} m</td></tr>
        </table>
      </div>
      <div class="card highlight">
        <h3>對外估價（板材）</h3>
        <p class="big">NT$ ${totalTwd.toLocaleString()}</p>
        <p class="muted small">${e.plankCount} × ${sku.priceTwdPerPiece} ／ 不含配件 / 工資</p>
      </div>
      <p class="muted small">⚠️ 訂製品仍須捲尺複丈、本估價僅供報價單參考。寶佳系客戶整案配套不適用。</p>
    </div>
  `;
  document.getElementById('back')!.addEventListener('click', () => { state.scene = 'edit'; render(); });
  document.getElementById('sku')!.addEventListener('change', (ev) => {
    state.selectedSkuId = (ev.target as HTMLSelectElement).value;
    render();
  });
  document.getElementById('export')!.addEventListener('click', () => {
    alert('Phase 2 接 PDF 模板、目前先 console.log');
    console.log({ poly, sku, estimate: e, totalTwd });
  });
}

render();
